import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Clock } from 'lucide-react';
import {
  segmentRangesFor,
  segmentBannerCopy,
  formatClock,
  STORY_SEGMENT_SECONDS,
} from '@/utils/storySegments';
import { tr } from '@/contexts/LanguageContext';

// ============================================================================
// SegmentsInfoCard
// ============================================================================
//
// Shown above the video preview when the picked clip is > 60s. It's the
// "your video will be split into 3 stories" Instagram-style card, with a
// row of mini thumbnails — one per segment — auto-captured from the
// video itself (frontend-only, no backend processing).
//
// PROPS
//   videoUrl         object URL of the picked file
//   durationSeconds  total duration (number)
//   onAction         optional click on the whole card
//
// Notes:
//   · Thumbnails are captured by seeking a hidden <video> to the
//     midpoint of each segment, drawing onto a canvas, and storing the
//     dataURL. This is a one-shot side-effect: we cancel cleanly on
//     unmount, and if the browser refuses to seek (some Safari builds)
//     we degrade to colored placeholder tiles so the layout doesn't
//     collapse. NEVER throws — the card always renders.

const THUMB_W = 64;
const THUMB_H = 96; // 9:16

const captureThumbnails = (videoUrl, ranges, onTile, onDone, abortRef) => {
  const v = document.createElement('video');
  v.preload = 'auto';
  v.muted = true;
  v.playsInline = true;
  v.crossOrigin = 'anonymous';
  v.src = videoUrl;

  const canvas = document.createElement('canvas');
  canvas.width = THUMB_W;
  canvas.height = THUMB_H;
  const ctx = canvas.getContext('2d');

  let i = 0;
  const next = () => {
    if (abortRef.current) return;
    if (i >= ranges.length) { onDone?.(); return; }
    const [a, b] = ranges[i];
    const mid = a + (b - a) / 2;
    const handler = () => {
      try {
        ctx.drawImage(v, 0, 0, THUMB_W, THUMB_H);
        onTile(i, canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        onTile(i, null); // CORS / tainted — fall back to placeholder
      }
      i += 1;
      v.removeEventListener('seeked', handler);
      next();
    };
    v.addEventListener('seeked', handler, { once: true });
    try { v.currentTime = mid; } catch { onTile(i, null); i += 1; next(); }
  };

  const onMeta = () => next();
  v.addEventListener('loadedmetadata', onMeta, { once: true });
  v.addEventListener('error', () => onDone?.(), { once: true });
};

export const SegmentsInfoCard = ({
  videoUrl,
  durationSeconds,
  onAction,
}) => {
  const ranges = segmentRangesFor(durationSeconds);
  const count = ranges.length;
  const [tiles, setTiles] = useState(() => ranges.map(() => null));
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    if (!videoUrl || count <= 1) return undefined;
    captureThumbnails(
      videoUrl,
      ranges,
      (i, dataUrl) => {
        if (abortRef.current) return;
        setTiles((prev) => {
          const next = prev.slice();
          next[i] = dataUrl;
          return next;
        });
      },
      undefined,
      abortRef,
    );
    return () => { abortRef.current = true; };
  }, [videoUrl, count]);

  if (count <= 1) return null;

  return (
    <motion.button
      type="button"
      onClick={onAction}
      data-testid="segments-info-card"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full text-left rounded-2xl border border-primary/30 bg-primary/[0.06] px-3 py-3 backdrop-blur-sm hover:border-primary/50 transition-colors"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 text-primary">
          <Layers className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <p
            className="text-xs font-bold text-foreground leading-tight"
            data-testid="segments-info-headline"
          >
            {segmentBannerCopy(count)}
          </p>
          <p className="text-[10px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />{tr("{0} · {1}s c/u", formatClock(durationSeconds), STORY_SEGMENT_SECONDS)}</p>
        </div>
      </div>

      {/* Thumbnail strip — one tile per segment */}
      <div
        className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1"
        data-testid="segments-info-thumbs"
      >
        {tiles.map((src, idx) => {
          const [a, b] = ranges[idx];
          const dur = Math.round(b - a);
          return (
            <div
              key={idx}
              className="relative flex-shrink-0 rounded-md overflow-hidden border border-white/10 bg-black/40"
              style={{ width: THUMB_W * 0.7, height: THUMB_H * 0.7 }}
              data-testid={`segments-info-thumb-${idx}`}
            >
              {src ? (
                <img
                  src={src}
                  alt={`Segmento ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
              )}
              <div className="absolute top-0.5 left-0.5 px-1 py-0.5 rounded text-[8px] font-bold bg-black/65 text-white leading-none">
                {idx + 1}/{count}
              </div>
              <div className="absolute bottom-0.5 right-0.5 px-1 py-0.5 rounded text-[8px] font-mono bg-black/65 text-white leading-none">
                {dur}s
              </div>
            </div>
          );
        })}
      </div>
    </motion.button>
  );
};

export default SegmentsInfoCard;
